/**
 * Invoice — renderer (frontend). Same shape as PurchaseOrder.tsx.
 * Reads the snapshot payload produced by backend/document-sharing/types/invoice.js.
 */
import type { DocumentProps, FrontendDocumentDefinition } from '../registry';
import { BaseDocumentLayout } from '../BaseDocumentLayout';

function formatCurrency(value: any): string {
  const n = typeof value === 'number' ? value : Number(value);
  if (!isFinite(n)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

function formatDate(value: any): string {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

const MUTED = 'rgb(var(--gray-500))'; // was #6b7280 — exact match to --gray-500 token

export const documentDefinition: FrontendDocumentDefinition = {
  docType: 'invoice',
  renderer: Invoice,
  appRoute: '/cost-management/invoices',
};

export function Invoice({ payload }: DocumentProps) {
  const inv = payload?.invoice || {};
  const company = payload?.company || null;
  const project = payload?.project || null;
  const client = payload?.client || null;
  const items: any[] = Array.isArray(payload?.items) ? payload.items : [];
  const subtotal = items.reduce((sum, it) => sum + (Number(it.total_price) || 0), 0);

  return (
    <BaseDocumentLayout
      company={company}
      title={(inv.type || 'INVOICE').toUpperCase()}
      reference={`#${inv.number}`}
      meta={[
        { label: 'Status', value: inv.status },
        { label: 'Due', value: formatDate(inv.due_date) },
      ]}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, marginBottom: 20 }}>
        <div style={{ flex: '1 1 240px' }}>
          {/* gap: this field-label style (fontSize 11 / uppercase / marginBottom 4) is
              repeated identically across all 6 document templates — candidate for a
              shared "doc field label" token/class, not added here per scope. */}
          <div style={{ fontSize: 11, textTransform: 'uppercase', color: MUTED, marginBottom: 4 }}>Client</div>
          <div style={{ fontWeight: 600 }}>{client?.name || 'Client'}</div>
          {client?.email && <div style={{ color: MUTED }}>{client.email}</div>}
          {client?.address && <div style={{ color: MUTED }}>{client.address}</div>}
        </div>
        <div style={{ flex: '1 1 240px' }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', color: MUTED, marginBottom: 4 }}>Project</div>
          <div style={{ fontWeight: 600 }}>{project?.name || '—'}</div>
        </div>
      </div>

      {inv.description && <div style={{ whiteSpace: 'pre-wrap', marginBottom: 20 }}>{inv.description}</div>}

      {items.length > 0 && (
        <div style={{ overflowX: 'auto', marginBottom: 20 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 480 }}>
            <thead>
              <tr style={{ background: 'rgb(var(--gray-100))', textAlign: 'left' }}>
                <th style={{ padding: '8px 10px', borderBottom: '1px solid rgb(var(--gray-300))' }}>Description</th>
                <th style={{ padding: '8px 10px', borderBottom: '1px solid rgb(var(--gray-300))', textAlign: 'right' }}>Qty</th>
                <th style={{ padding: '8px 10px', borderBottom: '1px solid rgb(var(--gray-300))', textAlign: 'right' }}>Unit Price</th>
                <th style={{ padding: '8px 10px', borderBottom: '1px solid rgb(var(--gray-300))', textAlign: 'right' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i} style={{ borderBottom: '1px solid rgb(var(--gray-200))' }}>
                  <td style={{ padding: '8px 10px' }}>{it.description || ''}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{it.quantity || 0}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{formatCurrency(it.unit_price)}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{formatCurrency(it.total_price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* gap: this footer total block (border-top 2px + paddingTop 12, fontSize 20/700
          value) is repeated identically across 5 of 6 document templates (all but
          Warranty) — candidate for a shared "doc total footer" token/component. */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '2px solid rgb(var(--gray-900))', paddingTop: 12 }}>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', color: MUTED }}>Total</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{formatCurrency(subtotal)}</div>
        </div>
      </div>
    </BaseDocumentLayout>
  );
}
